import * as React from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import GroupIcon from '@mui/icons-material/Group';
import WarningIcon from '@mui/icons-material/Warning';

import apiClient, { shouldRedirectToLogin, getErrorMessage } from '../services/api-client';
import AppConfig from '../services/app-config';
import AuthService from '../services/auth.js';
import { isReservedTeam } from '../services/systemTeams';
import SolarIcon from '../icons/SolarIcon';

function formatEquipoLabel(equipo) {
  const teams = Array.isArray(equipo) ? equipo : (equipo ? [equipo] : []);
  const filtered = teams.filter((t) => !isReservedTeam(t));
  if (filtered.length === 0) return null;
  if (filtered.length === 1) return filtered[0];
  return `${filtered[0]} +${filtered.length - 1}`;
}

// Resolve the effective allowed file types for gestiones: type config takes
// precedence, falling back to the org-level config from AppConfig.
function effectiveGestionAllowedTypes(typeConfig) {
  if (typeConfig && Array.isArray(typeConfig.tipos_archivo_permitidos) && typeConfig.tipos_archivo_permitidos.length > 0) {
    return typeConfig.tipos_archivo_permitidos;
  }
  const raw = AppConfig.get('gestiones_allowed_file_types');
  if (typeof raw === 'string' && raw.trim() !== '') {
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return ['image/jpeg', 'image/png', 'image/webp'];
}

function effectiveGestionThreadAllowedTypes(typeConfig) {
  if (typeConfig && Array.isArray(typeConfig.tipos_archivo_permitidos_thread) && typeConfig.tipos_archivo_permitidos_thread.length > 0) {
    return typeConfig.tipos_archivo_permitidos_thread;
  }
  // Fall back to tipos_archivo_permitidos
  if (typeConfig && Array.isArray(typeConfig.tipos_archivo_permitidos) && typeConfig.tipos_archivo_permitidos.length > 0) {
    return typeConfig.tipos_archivo_permitidos;
  }
  const raw = AppConfig.get('gestiones_allowed_file_types');
  if (typeof raw === 'string' && raw.trim() !== '') {
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return ['image/jpeg', 'image/png', 'image/webp'];
}

function effectiveGestionMaxMb(typeConfig) {
  if (typeConfig && typeConfig.tamano_max_archivo_mb) {
    return typeConfig.tamano_max_archivo_mb;
  }
  const raw = AppConfig.get('gestiones_max_file_mb');
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

const statusChipColors = {
  'reportado': { bg: '#fff3e0', color: '#e65100' },
  'en_progreso': { bg: '#e3f2fd', color: '#1565c0' },
  'en_revision': { bg: '#e3f2fd', color: '#1565c0' },
  'asignado': { bg: '#e8f5e9', color: '#2e7d32' },
  'programado': { bg: '#ede7f6', color: '#4527a0' },
  'en_espera': { bg: '#f3e5f5', color: '#7b1fa2' },
  'aprobado': { bg: '#e8f5e9', color: '#2e7d32' },
  'rechazado': { bg: '#fbe9e7', color: '#bf360c' },
  'resuelto': { bg: '#e8f5e9', color: '#2e7d32' },
  'completado': { bg: '#e8f5e9', color: '#2e7d32' },
  'cancelado': { bg: '#fbe9e7', color: '#bf360c' },
};

const timelineDotColors = {
  'reportado': 'grey',
  'en_progreso': 'primary',
  'en_revision': 'primary',
  'asignado': 'success',
  'programado': 'info',
  'en_espera': 'warning',
  'aprobado': 'success',
  'rechazado': 'error',
  'resuelto': 'success',
  'completado': 'success',
  'cancelado': 'error',
};

function getStateChip(estadoKey, estados) {
  const preset = statusChipColors[estadoKey];
  const friendly = estados && estados[estadoKey];
  const label = friendly ? friendly.nombre_corto : estadoKey;
  if (preset) return { bg: preset.bg, color: preset.color, label };
  return { bg: '#f5f5f5', color: '#616161', label };
}

// Format a custom-field value for display based on its type.
function formatFieldValue(value, type) {
  if (value === null || value === undefined || value === '') return '—';
  if (type === 'boolean') return value ? 'Sí' : 'No';
  if (type === 'date') {
    // Stored as YYYY-MM-DD (or epoch). Render dd/MMM/yyyy when possible.
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      const d = new Date(value + 'T00:00:00');
      if (!isNaN(d.getTime())) {
        const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
        return `${String(d.getDate()).padStart(2, '0')}/${months[d.getMonth()]}/${d.getFullYear()}`;
      }
    }
    if (typeof value === 'number') {
      return AppConfig.formatFullDate(value);
    }
    return String(value);
  }
  if (type === 'number') return String(value);
  return String(value);
}

// Compute the unread comentarios count for a gestion per the shared
// unread-mechanism: localStorage key `mgo:gestion-read:{id}` holds the epoch
// SECONDS of the last read timestamp. Comments from other users with a fecha
// newer than that are unread. If no key exists, all incoming comments from
// others are unread.
function computeUnreadCount(gestion, currentUserEmail) {
  const comentarios = (gestion && gestion.comentarios) || [];
  if (!comentarios.length) return 0;
  const readKey = `mgo:gestion-read:${gestion.id}`;
  let lastRead = null;
  try {
    const raw = localStorage.getItem(readKey);
    if (raw) lastRead = Number(raw);
  } catch (e) { /* ignore */ }
  let count = 0;
  for (const c of comentarios) {
    if (!c || c.usuario === currentUserEmail) continue;
    const fecha = Number(c.fecha);
    if (!Number.isFinite(fecha)) continue;
    if (lastRead === null || lastRead === undefined || isNaN(lastRead) || fecha > lastRead) {
      count += 1;
    }
  }
  return count;
}

// Inline-editable custom-field value. When `editable` is false (or the field
// type can't be edited inline — file/image), renders the plain read-only value.
// Click to edit: text/number/date show a small input committing on blur/Enter
// (Escape cancels), select commits on change, boolean toggles immediately.
function EditableFieldValue({ gestion, field, value, editable, onCampoSaved, setSnackbar }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const save = (newValue) => {
    setSaving(true);
    apiClient.post(`gestion/update/${gestion.id}`, { campos: { [field.key]: newValue } }, {}, { action: 'update_gestion_campo' })
      .then((response) => {
        if (response.status === 200) {
          onCampoSaved(field.key, newValue);
          setSnackbar({ open: true, message: 'Campo actualizado correctamente', severity: 'success' });
        }
        setSaving(false);
        setEditing(false);
      })
      .catch((error) => {
        setSnackbar({ open: true, message: getErrorMessage(error, 'Hubo un error al actualizar el campo.'), severity: 'error' });
        setSaving(false);
        setEditing(false);
      });
  };

  if (!editable) {
    return (
      <Typography variant="body2" sx={{ fontSize: 14, wordBreak: 'break-word' }}>
        {formatFieldValue(value, field.type)}
      </Typography>
    );
  }

  const currentStr = value === null || value === undefined ? '' : String(value);

  const commitDraft = () => {
    const newValue = field.type === 'number' ? (draft === '' ? '' : Number(draft)) : draft;
    if (String(newValue) === currentStr) {
      setEditing(false);
      return;
    }
    save(newValue);
  };

  const startEdit = () => {
    if (saving) return;
    if (field.type === 'boolean') {
      save(!value);
      return;
    }
    setDraft(currentStr);
    setEditing(true);
  };

  if (editing && field.type === 'select') {
    return (
      <FormControl fullWidth size="small" variant="standard">
        <Select
          autoFocus
          defaultOpen
          value={draft}
          disabled={saving}
          onChange={(e) => {
            if (String(e.target.value) === currentStr) {
              setEditing(false);
              return;
            }
            save(e.target.value);
          }}
          onClose={() => setEditing(false)}
          sx={{ fontSize: 14 }}
        >
          {(field.options || []).map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  if (editing) {
    return (
      <TextField
        size="small"
        variant="standard"
        autoFocus
        fullWidth
        type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
        value={draft}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commitDraft}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commitDraft();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setEditing(false);
          }
        }}
        InputProps={{ sx: { fontSize: 14 } }}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography
        variant="body2"
        onClick={startEdit}
        sx={{
          fontSize: 14, wordBreak: 'break-word', cursor: 'pointer',
          borderRadius: 0.5, px: 0.5, mx: -0.5,
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        {formatFieldValue(value, field.type)}
      </Typography>
      {saving && <CircularProgress size={14} />}
    </Box>
  );
}

// Render the custom-fields section, grouped by `seccion` when present.
// Hides fields where residente_visible === false for non-admin users.
function CustomFieldsSection({ gestion, typeConfig, isAdmin, canWrite, onCampoSaved, setSnackbar }) {
  if (!typeConfig || !typeConfig.campos_personalizados) return null;
  const campos = gestion.campos || {};
  const visibleFields = typeConfig.campos_personalizados.filter((f) => {
    if (f.residente_visible === false && !isAdmin) return false;
    return true;
  });
  if (visibleFields.length === 0) return null;

  // Group by seccion (preserve order; undefined-seccion fields go first under no header).
  const groups = [];
  const groupIndex = new Map();
  visibleFields.forEach((f) => {
    const sec = f.seccion || null;
    if (!groupIndex.has(sec)) {
      groupIndex.set(sec, groups.length);
      groups.push({ seccion: sec, fields: [] });
    }
    groups[groupIndex.get(sec)].fields.push(f);
  });

  return (
    <Box sx={{ mb: 1.5 }}>
      {groups.map((grp, gi) => (
        <Box key={`grp-${gi}`} sx={{ mb: gi < groups.length - 1 ? 1.5 : 0 }}>
          {grp.seccion && grp.seccion.toLowerCase() !== 'detalles' && (
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.5 }}>
              {grp.seccion}
            </Typography>
          )}
          {grp.fields.map((f) => {
            const editable = canWrite
              && (isAdmin || f.residente_editable === true)
              && f.type !== 'file'
              && f.type !== 'image';
            return (
              <Box key={f.key} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2, py: 0.4, alignItems: 'flex-start' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 14 }}>
                  {f.label}
                </Typography>
                <EditableFieldValue
                  gestion={gestion}
                  field={f}
                  value={campos[f.key]}
                  editable={editable}
                  onCampoSaved={onCampoSaved}
                  setSnackbar={setSnackbar}
                />
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}

// Admin-only assignees section: multi-select of teams to assign/unassign.
function AssigneesSection({ gestion, typeConfig, teams = {}, onAssign }) {
  const [selected, setSelected] = React.useState(gestion.equipos_asignados || []);
  React.useEffect(() => {
    setSelected(gestion.equipos_asignados || []);
  }, [gestion]);

  // Available teams come from the type's rbac_default keys (the teams the
  // operator configured). The backend enforces actual team existence.
  const availableTeams = React.useMemo(() => {
    if (!typeConfig || !typeConfig.rbac_default) return [];
    return Object.keys(typeConfig.rbac_default).filter((t) => !isReservedTeam(t));
  }, [typeConfig]);

  if (availableTeams.length === 0) return null;

  const teamNombre = (key) => (teams[key] && teams[key].nombre) || key;

  const handleChange = (event) => {
    const value = event.target.value;
    const next = typeof value === 'string' ? value.split(',') : value;
    setSelected(next);
    onAssign(next);
  };

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <GroupIcon sx={{ fontSize: 18 }} /> Equipos asignados
      </Typography>
      <FormControl fullWidth size="small">
        <InputLabel id="assignees-label">Equipos</InputLabel>
        <Select
          labelId="assignees-label"
          id="assignees-select"
          multiple
          value={selected}
          onChange={handleChange}
          label="Equipos"
          renderValue={(v) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(Array.isArray(v) ? v : []).map((t) => (
                <Chip key={t} label={teamNombre(t)} size="small" sx={{ height: 22, fontSize: '0.7rem' }} />
              ))}
            </Box>
          )}
        >
          {availableTeams.map((t) => (
            <MenuItem key={t} value={t}>
              {teamNombre(t)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

export default function Gestion({ setNavbarState, navbarState }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
  const [loading, setLoading] = React.useState(true);
  const [showBackdrop, setShowBackdrop] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
  const [refresh, setRefresh] = React.useState(false);
  const [gestion, setGestion] = React.useState({});
  const [typeConfig, setTypeConfig] = React.useState(null);
  const [teams, setTeams] = React.useState({});
  const [confirmDialog, setConfirmDialog] = React.useState({ open: false, action: null });
  const [deleteCommentDialog, setDeleteCommentDialog] = React.useState({ open: false, commentId: null });
  const [selectedNextState, setSelectedNextState] = React.useState('');

  const isAdmin = AuthService.userRoleIncludes(['admin']);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const triggerRefresh = () => setRefresh((r) => !r);

  const fetchTeams = React.useCallback(() => {
    apiClient.get('teams', {}, { action: 'fetch_teams' })
      .then((res) => {
        if (res.status === 200 && res.data && res.data.data) {
          setTeams(res.data.data.teams || {});
        }
      })
      .catch(() => {});
  }, []);

  const fetchTypeConfig = React.useCallback(async (typeKey) => {
    if (!typeKey) return null;
    try {
      const resp = await apiClient.get('gestiones/types', {}, { action: 'fetch_gestion_types' });
      if (resp.status === 200) {
        const types = (resp.data && resp.data.data && resp.data.data.types) || [];
        const cfg = types.find((t) => t.key === typeKey) || null;
        setTypeConfig(cfg);
        return cfg;
      }
    } catch (error) {
      console.error('Error fetching gestion types:', error);
      if (shouldRedirectToLogin(error)) {
        setRedirect({ trigger: true, route: '/login' });
      }
    }
    return null;
  }, []);

  const fetchGestion = React.useCallback(() => {
    setShowBackdrop(true);
    setLoading(true);
    apiClient.get(`gestion/get/${id}`, {}, { action: 'fetch_gestion' })
      .then(async (response) => {
        if (response.status === 200) {
          const data = response.data.data;
          const estados = data.estados || [];
          data.estado = estados[estados.length - 1] || { estado: '', fecha: 0 };
          setGestion(data);
          await fetchTypeConfig(data.type_key);
          setLoading(false);
          setShowBackdrop(false);
        }
      })
      .catch((error) => {
        console.error('Error fetching gestion:', error);
        if (shouldRedirectToLogin(error)) {
          setRedirect({ trigger: true, route: '/login' });
        } else {
          setSnackbar({ open: true, message: 'Hubo un error obteniendo los detalles de la gestión', severity: 'error' });
          setRedirect({ trigger: true, route: '/adminhub' });
        }
        setShowBackdrop(false);
        setLoading(false);
      });
  }, [id, fetchTypeConfig]);

  React.useEffect(() => {
    if (setNavbarState && navbarState) {
      setNavbarState({ ...navbarState, showBack: false, showOptions: false, showSearch: false });
    }
    fetchGestion();
    fetchTeams();
  }, [refresh]);

  const updateState = (newEstado) => {
    if (newEstado === 'cancelado') {
      setConfirmDialog({ open: true, action: () => performUpdateState(newEstado) });
    } else {
      performUpdateState(newEstado);
    }
  };

  const performUpdateState = (newEstado) => {
    setShowBackdrop(true);
    apiClient.post(`gestion/update/${id}`, { estado: newEstado }, {}, { action: 'update_gestion_state' })
      .then((response) => {
        if (response.status === 200) {
          setSnackbar({ open: true, message: 'Estado actualizado correctamente', severity: 'success' });
          triggerRefresh();
        }
        setShowBackdrop(false);
      })
      .catch((error) => {
        const msg = (error && error.data && error.data.data && error.data.data.mensaje)
          || getErrorMessage(error, 'Hubo un error al actualizar el estado.');
        setSnackbar({ open: true, message: msg, severity: 'error' });
        setShowBackdrop(false);
      });
  };

  const handleAssign = (equipos) => {
    apiClient.post(`gestion/update/${id}`, { equipos_asignados: equipos }, {}, { action: 'update_gestion_assignees' })
      .then((response) => {
        if (response.status === 200) {
          setSnackbar({ open: true, message: 'Asignación actualizada correctamente', severity: 'success' });
          setGestion((g) => ({ ...g, equipos_asignados: equipos }));
        }
      })
      .catch((error) => {
        const msg = (error && error.data && error.data.data && error.data.data.mensaje)
          || getErrorMessage(error, 'Hubo un error al actualizar la asignación.');
        setSnackbar({ open: true, message: msg, severity: 'error' });
      });
  };

  // Update a single custom field locally after an inline-edit save — no
  // backdrop, no full refresh (the WS broadcast keeps other viewers in sync).
  const onCampoSaved = (fieldKey, value) => {
    setGestion((g) => ({ ...g, campos: { ...(g.campos || {}), [fieldKey]: value } }));
  };

  const toggleSubscription = (e) => {
    setShowBackdrop(true);
    const enable = e.target.checked ? 'true' : 'false';
    apiClient.post(`gestiones/subscribe/${id}/${enable}`, {}, {}, { action: 'toggle_gestion_subscription' })
      .then((response) => {
        if (response.status === 200) {
          setGestion({ ...gestion, subscribed: response.data.data.enabled });
          setSnackbar({ open: true, message: 'Notificaciones actualizadas correctamente', severity: 'success' });
        }
        setShowBackdrop(false);
      })
      .catch((error) => {
        console.error('Error toggling subscription:', error);
        setSnackbar({ open: true, message: 'Hubo un error al actualizar las notificaciones.', severity: 'error' });
        setShowBackdrop(false);
      });
  };

  // LiveThread callbacks — the parent owns the API calls; LiveThread owns
  // the input state (text + files) and passes them via onSubmitComment.
  const handleLiveSubmitComment = ({ text, files }) => {
    if (text.trim() === '' && (!files || files.length === 0)) {
      setSnackbar({ open: true, message: 'No puedes enviar un comentario vacío.', severity: 'error' });
      return;
    }
    setShowBackdrop(true);
    const formData = new FormData();
    formData.append('comentario', text);
    (files || []).forEach((f) => formData.append('adjuntos', f));
    apiClient.post(`gestion/comment/${id}`, formData, {}, { action: 'add_gestion_comment' })
      .then((response) => {
        if (response.status === 200) {
          setSnackbar({ open: true, message: 'Comentario agregado', severity: 'success' });
          triggerRefresh();
        }
        setShowBackdrop(false);
      })
      .catch((error) => {
        const msg = (error && error.data && error.data.data && error.data.data.mensaje)
          || getErrorMessage(error, 'Hubo un error al agregar tu comentario.');
        setSnackbar({ open: true, message: msg, severity: 'error' });
        setShowBackdrop(false);
      });
  };

  const handleLiveCommentsChange = (updater) => {
    setGestion((prev) => {
      const prevList = (prev && prev.comentarios) ? prev.comentarios : [];
      const next = typeof updater === 'function' ? updater(prevList) : updater;
      return { ...prev, comentarios: next };
    });
  };

  const handleLiveEntityUpdate = () => {
    // Refresh the gestion from the server to pick up state / assignee / field changes.
    triggerRefresh();
  };

  const handleLiveDeleteComment = (comentario) => {
    setDeleteCommentDialog({ open: true, commentId: comentario.fecha });
  };

  const handleOpenDeleteDialog = (commentId) => {
    setDeleteCommentDialog({ open: true, commentId });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteCommentDialog({ open: false, commentId: null });
  };

  const handleDeleteComment = () => {
    setShowBackdrop(true);
    apiClient.post(`gestion/delete/comment/${id}`, { comentario_id: deleteCommentDialog.commentId }, {}, { action: 'delete_gestion_comment' })
      .then((response) => {
        if (response.status === 200) {
          setSnackbar({ open: true, message: 'Comentario eliminado correctamente', severity: 'success' });
          triggerRefresh();
        }
        setShowBackdrop(false);
        handleCloseDeleteDialog();
      })
      .catch((error) => {
        console.error('Error deleting comment:', error);
        setSnackbar({ open: true, message: 'Hubo un error al intentar eliminar el comentario.', severity: 'error' });
        setShowBackdrop(false);
        handleCloseDeleteDialog();
      });
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ open: false, action: null });
  };

  // Determine whether the current user can write (change state / assign).
  // Backend is source of truth; this is a best-effort UX gate.
  const canWrite = (() => {
    if (!typeConfig) return false;
    if (isAdmin) return true;
    // Residente-editable types allow the creator (habitante) to update state
    // along the configured transitions.
    if (gestion.creador && gestion.creador.usuario === AuthService.getCurrentUserEmail()) {
      return true;
    }
    return false;
  })();

  const estados = (typeConfig && typeConfig.estados) || {};
  const currentState = gestion.estado && gestion.estado.estado;
  const currentStateCfg = estados[currentState] || null;
  const nextStates = (currentStateCfg && currentStateCfg.siguiente_estado) || [];
  const selectedNextStateCfg = selectedNextState ? (estados[selectedNextState] || {}) : {};
  const selectedNextStateBlocked = selectedNextStateCfg.requiere_asignacion === true && !(gestion.equipos_asignados && gestion.equipos_asignados.length > 0);

  const attachmentBasename = (path) => (typeof path === 'string' ? path.split('/').pop() : String(path));

  // Unread comentarios count for the chat badge (§4.11).
  const currentUserEmail = AuthService.getCurrentUserEmail();
  const unreadCount = computeUnreadCount(gestion, currentUserEmail);
  // Earliest estado fecha for the "Creado" row (§4.2).
  const creadoFecha = (gestion.estados && gestion.estados[0] && gestion.estados[0].fecha) || (gestion.estado && gestion.estado.fecha);

  return (
    <Container maxWidth="sm" disableGutters>
      {redirect.trigger && <Navigate to={redirect.route} replace={true} />}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: '3.2rem' }}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Backdrop open={showBackdrop} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ mt: 0 }}>
        {!loading && gestion && gestion.id && (
          <Card variant="outlined" sx={{ borderRadius: '0 0 16px 16px', border: 'none', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ px: 2.5, pt: 2.5 }}>
              {/* Header — back button (left) + chat icon with unread badge (right) */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <IconButton onClick={() => navigate(-1)} sx={{ color: 'text.primary', ml: -1 }} size="small">
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <IconButton onClick={() => navigate(`/gestion/${id}/chat`)} size="small" sx={{ color: 'secondary.main' }}>
                  <Badge badgeContent={unreadCount} color="error">
                    <SolarIcon name="chat-round-dots" size={24} color="secondary.main" />
                  </Badge>
                </IconButton>
              </Box>

              {/* Title */}
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                {gestion.titulo}
              </Typography>

              {/* Description */}
              {gestion.descripcion && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                  {gestion.descripcion}
                </Typography>
              )}

              {/* Section "Gestión" — 2-column grid (Ticket / Creado / Estado) */}
              <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 1 }}>
                Gestión
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2, rowGap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 14 }}>Ticket</Typography>
                <Typography variant="body2" sx={{ fontSize: 14 }}>{gestion.id}</Typography>

                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 14 }}>Creado</Typography>
                <Typography variant="body2" sx={{ fontSize: 14 }}>{AppConfig.formatFullDate(creadoFecha)}</Typography>

                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 14 }}>Estado</Typography>
                <Box>
                  <Chip
                    label={currentStateCfg ? currentStateCfg.nombre_corto : (estados[currentState] ? estados[currentState].nombre_corto : currentState)}
                    size="small"
                    sx={{ fontWeight: 700, fontSize: '0.75rem', backgroundColor: 'rgba(233,203,167,0.5)', color: '#F2700C', borderRadius: 1 }}
                  />
                </Box>
              </Box>

              {/* Subscription — 25px below the "Gestión" section */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: '25px' }}>
                <Checkbox checked={!!gestion.subscribed} onChange={toggleSubscription} size="small" />
                <Typography variant="caption" color="text.secondary">
                  Recibir notificaciones sobre esta gestión
                </Typography>
              </Box>

              {/* Gray divider — 25px separation */}
              <Divider sx={{ mt: '25px' }} />

              {/* Section "Detalles" — 25px after divider */}
              <Typography sx={{ fontSize: 18, fontWeight: 800, mt: '25px', mb: 1 }}>
                Detalles
              </Typography>
              <CustomFieldsSection
                gestion={gestion}
                typeConfig={typeConfig}
                isAdmin={isAdmin}
                canWrite={canWrite}
                onCampoSaved={onCampoSaved}
                setSnackbar={setSnackbar}
              />

              {/* Section "Adjuntos" — 25px after "Detalles", 20px before attachments */}
              {gestion.adjuntos && gestion.adjuntos.length > 0 && (
                <Box sx={{ mb: 1.5, mt: '25px' }}>
                  <Typography sx={{ fontSize: 18, fontWeight: 800, mb: '20px' }}>
                    Adjuntos
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {gestion.adjuntos.map((path, i) => (
                      <Chip
                        key={`att-${i}`}
                        icon={<AttachFileIcon sx={{ fontSize: '0.9rem' }} />}
                        label={attachmentBasename(path)}
                        size="small"
                        component="a"
                        href={`/api/gestiones/media/${String(path).replace(/^\/+/, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        clickable
                        sx={{ height: 24, fontSize: '0.7rem', textDecoration: 'none' }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Assignees (admin only) */}
              {isAdmin && (
                <>
                  <Divider sx={{ mb: 1.5 }} />
                  <AssigneesSection gestion={gestion} typeConfig={typeConfig} teams={teams} onAssign={handleAssign} />
                </>
              )}

              {/* Gray divider — 25px separation */}
              <Divider sx={{ mt: '25px' }} />

              {/* Section "Seguimiento" — 25px after divider */}
              <Typography sx={{ fontSize: 18, fontWeight: 800, mt: '25px', mb: 1 }}>
                Seguimiento
              </Typography>
              <Timeline position="right" sx={{ p: 0, m: 0 }}>
                {(gestion.estados || []).slice().reverse().map((estado, index) => (
                  <TimelineItem key={`estado-${index}`} sx={{ minHeight: 50 }}>
                    <TimelineOppositeContent sx={{ flex: 0.3, py: 0.5, px: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {AppConfig.formatFriendlyDateWithYear(estado.fecha)}
                      </Typography>
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot color={timelineDotColors[estado.estado] || 'grey'} sx={{ my: 0.5, width: 10, height: 10, p: 0 }} />
                      {index !== gestion.estados.length - 1 && <TimelineConnector sx={{ minHeight: 20 }} />}
                    </TimelineSeparator>
                    <TimelineContent sx={{ py: 0.5, px: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                        {estados[estado.estado] ? estados[estado.estado].nombre_corto : estado.estado}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <PersonIcon sx={{ fontSize: 12 }} /> {estado.usuario_nombre}
                        {formatEquipoLabel(estado.equipo) && (
                          <Chip label={formatEquipoLabel(estado.equipo)} size="small" sx={{ ml: 0.5, height: 16, fontSize: '0.6rem' }} />
                        )}
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>

            </CardContent>

            {/* State transition buttons — dropdown when multiple options, single button otherwise */}
            {isAdmin && canWrite && nextStates.length > 0 && (
              <CardActions sx={{ px: 2.5, pb: 2.5, pt: 1, gap: 1, alignItems: 'flex-end' }}>
                {nextStates.length === 1 ? (
                  (() => {
                    const estado = nextStates[0];
                    const targetCfg = estados[estado] || {};
                    const isCancel = estado === 'cancelado';
                    const requiresAsignacion = targetCfg.requiere_asignacion === true;
                    const hasEquipos = (gestion.equipos_asignados && gestion.equipos_asignados.length > 0);
                    const blockedByAsignacion = requiresAsignacion && !hasEquipos;
                    const buttonEl = (
                      <Button
                        variant={isCancel ? 'outlined' : 'contained'}
                        fullWidth
                        disabled={blockedByAsignacion}
                        startIcon={isCancel ? <CancelOutlinedIcon /> : (blockedByAsignacion ? <WarningIcon /> : <CheckCircleOutlinedIcon />)}
                        onClick={() => updateState(estado)}
                        sx={{
                          borderRadius: 2, textTransform: 'none', fontWeight: 600,
                          ...(isCancel
                            ? { borderColor: '#d32f2f', color: '#d32f2f', '&:hover': { borderColor: '#b71c1c', backgroundColor: '#fbe9e7' } }
                            : { backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#1b5e20' } })
                        }}
                      >
                        {targetCfg.nombre_accion || targetCfg.nombre_corto || estado}
                      </Button>
                    );
                    return blockedByAsignacion ? (
                      <Tooltip title="Requiere equipo asignado" placement="top">
                        <span style={{ width: '100%' }}>{buttonEl}</span>
                      </Tooltip>
                    ) : (
                      <React.Fragment>{buttonEl}</React.Fragment>
                    );
                  })()
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'flex-end' }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="state-transition-label">Cambiar estado</InputLabel>
                      <Select
                        labelId="state-transition-label"
                        id="state-transition-select"
                        value={selectedNextState}
                        label="Cambiar estado"
                        onChange={(e) => setSelectedNextState(e.target.value)}
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                      >
                        {nextStates.map((estado) => {
                          const targetCfg = estados[estado] || {};
                          const requiresAsignacion = targetCfg.requiere_asignacion === true;
                          const hasEquipos = (gestion.equipos_asignados && gestion.equipos_asignados.length > 0);
                          const blockedByAsignacion = requiresAsignacion && !hasEquipos;
                          return (
                            <MenuItem
                              key={estado}
                              value={estado}
                              disabled={blockedByAsignacion}
                              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                              {estado === 'cancelado'
                                ? <CancelOutlinedIcon sx={{ fontSize: 18, color: '#d32f2f' }} />
                                : blockedByAsignacion
                                  ? <WarningIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                                  : <CheckCircleOutlinedIcon sx={{ fontSize: 18, color: '#2e7d32' }} />}
                              {targetCfg.nombre_accion || targetCfg.nombre_corto || estado}
                              {blockedByAsignacion && ' (requiere equipo)'}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    <Tooltip
                      title={selectedNextStateBlocked ? 'Requiere equipo asignado' : ''}
                      placement="top"
                      open={selectedNextStateBlocked && !!selectedNextState}
                    >
                      <span>
                        <Button
                          variant={selectedNextState === 'cancelado' ? 'outlined' : 'contained'}
                          disabled={!selectedNextState || selectedNextStateBlocked}
                          startIcon={selectedNextState === 'cancelado' ? <CancelOutlinedIcon /> : <CheckCircleOutlinedIcon />}
                          onClick={() => selectedNextState && updateState(selectedNextState)}
                          sx={{
                            borderRadius: 2, textTransform: 'none', fontWeight: 600, minWidth: 120, whiteSpace: 'nowrap',
                            ...(selectedNextState === 'cancelado'
                              ? { borderColor: '#d32f2f', color: '#d32f2f', '&:hover': { borderColor: '#b71c1c', backgroundColor: '#fbe9e7' } }
                              : { backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#1b5e20' } })
                          }}
                        >
                          Aplicar
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                )}
              </CardActions>
            )}
          </Card>
        )}
      </Box>

      <Dialog
        open={confirmDialog.open}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          ¿Estás seguro de que quieres cancelar esta gestión?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <Box display="flex" alignItems="center">
              <WarningIcon color="warning" sx={{ mr: 1 }} />
              La gestión quedará cancelada y el estado quedará en el historial.
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">No, mantener</Button>
          <Button
            onClick={() => { confirmDialog.action && confirmDialog.action(); handleCloseConfirmDialog(); }}
            color="secondary"
            variant="contained"
            autoFocus
          >
            Sí, cancelar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteCommentDialog.open}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="del-dialog-title"
        aria-describedby="del-dialog-description"
      >
        <DialogTitle id="del-dialog-title">
          ¿Estás seguro de que quieres eliminar este comentario?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="del-dialog-description">
            Esta acción no se puede deshacer. El comentario será eliminado permanentemente.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">Cancelar</Button>
          <Button onClick={handleDeleteComment} color="secondary" variant="contained" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
